import {
  GetAuthorWithArticlesDocument,
  Locale,
  type GetAuthorWithArticlesQuery,
} from '@seek/cmsu-cms-connect';
import type { LoaderFunction } from 'react-router';

import {
  getRecordsToSkip,
  pageConfig,
} from '../client/shared/utils/Pagination';
import { getLocales } from '../client/shared/utils/getLocales';
import { getHygraphLocales } from '../client/shared/utils/helper';
import {
  getAuthorWithArticlesFromData,
  getPreviewProps,
} from '../helpers/loadersHelper';
import { internalError, notFound } from '../responses';
import type { RouteProps } from '../routes';
import { json } from '../server/utils/json';

/**
 * This loader is used to fetch an author with articles content from HyGraph.
 * @param config
 * @param client
 */
export const authorWithArticlesLoader =
  ({ config, client }: RouteProps): LoaderFunction =>
  async ({ params }) => {
    // Extract variables
    const { site, hygraphLocale, hygraphSite, language, pathname, isDraft } =
      config;
    const { slug, pageNumber } = params;
    const { stage, queryOptions } = getPreviewProps(isDraft);

    const skip = getRecordsToSkip(pageNumber);

    // Query hygraph
    const variables = {
      slug: decodeURIComponent(slug || ''),
      site: hygraphSite,
      locale: getHygraphLocales(hygraphLocale),
      localesWithDefault: [hygraphLocale, Locale.Default],
      stage,
      first: pageConfig.pageSize,
      skip,
    };

    // Fetch the author if it exists.
    const { data, error } = await client.query<GetAuthorWithArticlesQuery>({
      query: GetAuthorWithArticlesDocument,
      variables,
      ...queryOptions,
    });

    if (error) {
      throw internalError(error);
    }

    // If the content is not available, redirect user to NotFound Page (CU-762).
    if (!data.authors.length) {
      throw notFound(slug);
    }

    const authors = getAuthorWithArticlesFromData(data, hygraphLocale);

    // If the content is not available, redirect to NotFound Page (CU-762).
    if (!authors || !authors.name) {
      throw notFound(slug);
    }

    // ensure we have article now
    const locales = getLocales(
      site,
      pathname.replace(`/${language}/`, ''),
      data.authors[0].sites,
      data.authors[0].localizations,
    );

    return json({ ...authors, locales });
  };
